/**
 * <AccountPickerTile> — une tuile de « Qui utilise Masteur ? » (molecule).
 *
 * Un visage en grand et un mot dessous. Trois visages, une seule empreinte et un seul geste :
 * un compte (son avatar, la capsule pour un tuteur), l'« Espace parent » (l'image de l'app, dans
 * l'anneau de son univers) et « Ajouter un compte » (un plus, sans visage).
 *
 * `loading` dit que la personne vient de toucher CETTE tuile : le visage s'estompe et le mot se
 * souligne en boucle (`<WaitingText>`), sans roue. `disabled` la fige sans rien changer à son
 * aspect : c'est une autre tuile qui travaille.
 *
 * Le mode de retrait (`onRemove`) : la tuile porte la marque du retrait, un « − » rouge au coin du
 * visage, et son appui DEMANDE le retrait au lieu d'ouvrir. Toute la tuile presse, comme à
 * l'ouverture : un seul bouton, que le lecteur d'écran nomme « Retirer {prénom} ». Wajih,
 * 21/10 : un petit « Retirer » sous le prénom ne disait pas assez qu'on était entré dans le mode.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { FocusRing, focusRingHost } from "../../atoms/FocusRing"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import parentSpaceImage from "../../../../assets/avatar/default/parent-512.png"

/** Le diamètre du visage — le 120 de l'écran d'aujourd'hui. */
const ACCOUNT_PICKER_FACE_SIZE = 120
/** L'empreinte d'une tuile : deux tiennent côte à côte sur un téléphone de 390 px. */
const ACCOUNT_PICKER_TILE_WIDTH = 160
const PLUS_SIZE = 40
/** La marque du retrait, au coin haut droit du visage. */
const REMOVE_MARK_SIZE = 34
const REMOVE_ICON_SIZE = 20

type TileState = {
    /** La tuile vient d'être touchée : son visage s'estompe, son mot se souligne, elle ne répond plus. */
    loading?: boolean
    /** Une autre tuile travaille : celle-ci ne répond plus, sans changer d'aspect. */
    disabled?: boolean
    onPress?: () => void
    testID?: string
}

/** Un compte : son prénom, son visage, et en mode « Gérer les comptes » hors famille, « Retirer ». */
type AccountTile = {
    variant?: "account"
    /** Le prénom sous le visage. */
    label: string
    /** L'image du compte, servie par le serveur. Sans image, l'initiale du prénom. */
    avatarUri?: string
    /** Un tuteur porte la capsule (liseré blanc, nœud jaune). */
    tutor?: boolean
    /**
     * Le mode de retrait : la marque « − » au coin du visage, et l'appui demande le retrait au lieu
     * d'ouvrir. Absent → la tuile ouvre.
     */
    onRemove?: () => void
}

/** « Espace parent » et « Ajouter un compte » dessinent eux-mêmes leur mot et leur visage. */
export type AccountPickerTileProps = TileState & (AccountTile | { variant: "parentSpace" } | { variant: "add" })

type PressableVisualState = { pressed: boolean; focused?: boolean }

/** L'encre du mot : le prénom d'un compte, l'univers de l'espace parent, un ajout plus discret. */
const LABEL_COLOR = {
    account: "text-primary",
    parentSpace: "role-parent",
    add: "text-muted",
} as const

const FIXED_LABEL = { parentSpace: "Espace parent", add: "Ajouter un compte" } as const

export function AccountPickerTile(props: AccountPickerTileProps) {
    const { loading = false, disabled = false, onPress, testID } = props
    const variant = props.variant ?? "account"
    const account = props.variant === "parentSpace" || props.variant === "add" ? null : props
    const label = account != null ? account.label : FIXED_LABEL[props.variant as "parentSpace" | "add"]
    const onRemove = account?.onRemove
    const removing = onRemove != null

    const inert = disabled || loading
    const pressLabel = removing ? `Retirer ${label}` : label

    const face =
        variant === "add" ? (
            <View style={styles.addFace}>
                <Icon name="plus" size={PLUS_SIZE} color="text-muted" />
            </View>
        ) : (
            <Avatar
                uri={account?.avatarUri}
                source={account == null ? parentSpaceImage : undefined}
                name={label}
                size={ACCOUNT_PICKER_FACE_SIZE}
                variant={account?.tutor === true ? "tutor" : "default"}
                ringColor={account == null ? "role-parent" : undefined}
                dimmed={loading}
            />
        )

    // Le même mot au repos et pendant l'ouverture : rien ne change de graisse, d'encre ni de lignes.
    const labelProps = {
        size: "text-lg",
        weight: variant === "parentSpace" ? "bold" : "semibold",
        color: LABEL_COLOR[variant],
        numberOfLines: 2,
    } as const

    return (
        <View testID={testID} style={styles.tile}>
            <Pressable
                onPress={removing ? onRemove : onPress}
                disabled={inert}
                accessibilityRole="button"
                accessibilityLabel={pressLabel}
                accessibilityState={{ disabled: inert, busy: loading }}
                // react-native-web ne traduit pas `busy` : sans lui, l'ouverture ne s'annoncerait pas.
                aria-busy={loading}
                style={({ pressed }: PressableVisualState) => [
                    styles.press,
                    focusRingHost,
                    pressed && !inert ? styles.pressed : undefined,
                ]}
            >
                {({ focused }: PressableVisualState) => (
                    <>
                        <View style={styles.face}>
                            {face}
                            <FocusRing visible={focused === true && !inert} radius={ACCOUNT_PICKER_FACE_SIZE / 2} />
                            {removing && (
                                <View style={styles.removeMark}>
                                    <Icon name="minus" size={REMOVE_ICON_SIZE} color="white" />
                                </View>
                            )}
                        </View>
                        {/* Pendant l'ouverture, le prénom se souligne en boucle : l'attente se dit sur la
                            tuile touchée, sans roue posée sur le visage. */}
                        {loading ? (
                            // La tuile annonce déjà l'attente (`busy`, son nom) : les mots ne la redisent pas.
                            <WaitingText {...labelProps} announce={false}>
                                {label}
                            </WaitingText>
                        ) : (
                            <Text {...labelProps} center>
                                {label}
                            </Text>
                        )}
                    </>
                )}
            </Pressable>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    tile: {
        width: ACCOUNT_PICKER_TILE_WIDTH,
    },
    press: {
        alignItems: "center",
        gap: theme.spacing["3"],
    },
    pressed: {
        transform: [{ scale: 0.98 }],
    },
    face: {
        width: ACCOUNT_PICKER_FACE_SIZE,
        height: ACCOUNT_PICKER_FACE_SIZE,
    },
    addFace: {
        width: ACCOUNT_PICKER_FACE_SIZE,
        height: ACCOUNT_PICKER_FACE_SIZE,
        borderRadius: ACCOUNT_PICKER_FACE_SIZE / 2,
        borderWidth: 2,
        borderStyle: "dashed",
        borderColor: theme.colors.border,
        alignItems: "center",
        justifyContent: "center",
    },
    removeMark: {
        position: "absolute",
        top: 0,
        right: 0,
        width: REMOVE_MARK_SIZE,
        height: REMOVE_MARK_SIZE,
        borderRadius: REMOVE_MARK_SIZE / 2,
        borderWidth: 3,
        borderColor: theme.colors.surface,
        backgroundColor: theme.colors["danger-fill"],
        alignItems: "center",
        justifyContent: "center",
    },
}))
